import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  ArrowLeft, Play, Pause, SkipBack, SkipForward, Rewind,
  FastForward, Sun, Moon, Trophy, Flag,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const LAYOUT_NAMES = {
  classic: "Old Classic", gauntlet: "Gauntlet",
  open_field: "Open Field", fortress: "Classic Fortress",
  mini: "Mini", boardgame: "Original", boardgame2: "Fortress", fortress2: "Fortress 2.0", infinity: "Infinity (old)", infinity2: "Infinity", boardgame3: "Classic", boardgame4: "Firefly (old)", firefly2: "Firefly", boardgame5: "The Wave", bridges: "Bridges (old)", bridges_trial: "Bridges (short)", bridges2: "Bridges", bridges3: "Bridges 2.0",
};
const ROWS = 20;
const COLS = 13;
const BASE_COLS = [4, 5, 6, 7, 8];
const TOP_ROW = 0;
const BOTTOM_ROW = 19;

function specClass(s) {
  if (s === "WALL") return "bg-foreground/85";
  if (s === "PINK") return "bg-pink-400/20 border-pink-400/50";
  if (s === "GREEN") return "bg-emerald-400/20 border-emerald-400/50";
  return "";
}

const SPEEDS = [
  { label: "0.5×", ms: 1600 },
  { label: "1×", ms: 800 },
  { label: "2×", ms: 400 },
  { label: "4×", ms: 150 },
];

const ACTION_LABEL = {
  start: "Game start",
  place: "Placed a tile",
  pickup: "Picked up a tile",
  peel: "Played",
  forfeit: "Forfeited",
};

export default function Replay() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { theme, toggle: toggleTheme } = useTheme();

  const [replay, setReplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speedIdx, setSpeedIdx] = useState(1);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/match/${matchId}/replay`);
        setReplay(data);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Replay not available");
        navigate("/profile");
      } finally {
        setLoading(false);
      }
    })();
  }, [matchId, navigate]);

  const frames = replay?.frames || [];
  const total = frames.length;
  const frame = frames[idx];

  // Playback loop
  useEffect(() => {
    if (!playing) return;
    if (idx >= total - 1) {
      setPlaying(false);
      return;
    }
    timerRef.current = setTimeout(() => {
      setIdx((i) => Math.min(i + 1, total - 1));
    }, SPEEDS[speedIdx].ms);
    return () => clearTimeout(timerRef.current);
  }, [playing, idx, total, speedIdx]);

  const togglePlay = useCallback(() => {
    if (idx >= total - 1) {
      setIdx(0);
      setPlaying(true);
    } else {
      setPlaying((p) => !p);
    }
  }, [idx, total]);

  if (loading || !replay) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground" data-testid="replay-loading">
        Loading replay…
      </div>
    );
  }

  const spec = replay.spec || {};
  const tiles = frame?.tiles || {};
  const layout = replay.layout || "classic";

  const winnerName = replay.winner_color === "A"
    ? replay.player1_username
    : replay.winner_color === "B"
    ? replay.player2_username
    : "Draw";

  const cells = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const key = `${r},${c}`;
      const cellSpec = spec[key];
      const tile = tiles[key];
      const isTopBase = r === TOP_ROW && BASE_COLS.includes(c);
      const isBottomBase = r === BOTTOM_ROW && BASE_COLS.includes(c);
      const baseRing = isTopBase
        ? "ring-1 ring-inset ring-[hsl(var(--chart-3))]/60"
        : isBottomBase
        ? "ring-1 ring-inset ring-brand/60"
        : "";
      const isWall = cellSpec === "WALL";
      cells.push(
        <div
          key={key}
          data-testid={`replay-cell-${r}-${c}`}
          className={`relative aspect-square select-none border border-border/60 rounded-[5px]
            ${specClass(cellSpec)} ${baseRing}
            text-[11px] md:text-[13px] font-display font-black flex items-center justify-center`}
        >
          {!isWall && tile && (
            <span
              className={`w-[88%] h-[88%] rounded-[4px] flex items-center justify-center transition-all
                ${tile.color === "A" ? "bg-[hsl(var(--chart-3))] text-background" : "bg-brand text-brand-foreground"}
                shadow-sm`}
            >
              {tile.letter}
            </span>
          )}
        </div>
      );
    }
  }

  const actorName = frame?.actor === "A"
    ? replay.player1_username
    : frame?.actor === "B"
    ? replay.player2_username
    : null;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="replay-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost" size="sm"
          onClick={() => navigate(-1)}
          className="rounded-full -ml-2 text-muted-foreground hover:text-foreground"
          data-testid="replay-back-btn"
        >
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <div className="text-center">
          <h1 className="font-display text-xl md:text-2xl font-black">Match Replay</h1>
          <p className="text-xs text-muted-foreground uppercase tracking-[0.2em] font-bold">
            {LAYOUT_NAMES[layout] || layout} · {replay.mode === "turn_based" ? "Turn-based" : "Real-time"}
          </p>
        </div>
        <Button
          variant="ghost" size="icon" onClick={toggleTheme}
          className="rounded-full"
          data-testid="replay-theme-toggle"
        >
          {theme === "dark" ? <Sun size={16} weight="fill" /> : <Moon size={16} weight="fill" />}
        </Button>
      </div>

      {/* Players line */}
      <div className="flex items-center justify-center gap-6 mb-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-[hsl(var(--chart-3))]" />
          <span className="font-display font-bold">{replay.player1_username}</span>
          <span className="text-muted-foreground text-xs">{replay.player1_elo} ELO</span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground/70">vs</span>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-brand" />
          <span className="font-display font-bold">{replay.player2_username}</span>
          <span className="text-muted-foreground text-xs">{replay.player2_elo} ELO</span>
        </div>
      </div>

      {/* Winner banner */}
      {replay.winner_color && (
        <div className="text-center mb-4 text-sm">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand/10 text-brand font-display font-bold">
            <Trophy size={14} weight="fill" /> {winnerName} won — {replay.win_reason}
          </span>
        </div>
      )}

      {/* Top base label */}
      <div className="text-center text-[10px] uppercase tracking-[0.3em] font-bold py-1 text-[hsl(var(--chart-3))]">
        ▲ {replay.player1_username}'s base
      </div>

      {/* Board */}
      <div className="flex items-center justify-center mb-2">
        <div
          data-testid="replay-board"
          className="grid gap-[3px] bg-border/40 p-[3px] rounded-lg shadow-xl"
          style={{
            gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
            width: "min(94vw, 56vh)",
            aspectRatio: `${COLS} / ${ROWS}`,
          }}
        >
          {cells}
        </div>
      </div>

      <div className="text-center text-[10px] uppercase tracking-[0.3em] font-bold py-1 text-brand">
        ▼ {replay.player2_username}'s base
      </div>

      {/* Controls */}
      <div className="mt-5 rounded-2xl border border-border bg-surface/60 p-4 space-y-3">
        {/* Scrubber */}
        <div className="flex items-center gap-3" data-testid="replay-scrubber-row">
          <span className="text-xs text-muted-foreground font-mono w-16 text-right">
            {idx + 1} / {total}
          </span>
          <Slider
            value={[idx]}
            min={0}
            max={Math.max(0, total - 1)}
            step={1}
            onValueChange={(v) => { setPlaying(false); setIdx(v[0] ?? 0); }}
            className="flex-1"
            data-testid="replay-scrubber"
          />
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="icon" className="rounded-full"
              onClick={() => { setPlaying(false); setIdx(0); }}
              data-testid="replay-start-btn"
            >
              <SkipBack size={16} weight="fill" />
            </Button>
            <Button
              variant="ghost" size="icon" className="rounded-full"
              onClick={() => { setPlaying(false); setIdx((i) => Math.max(0, i - 1)); }}
              data-testid="replay-prev-btn"
            >
              <Rewind size={16} weight="fill" />
            </Button>
            <Button
              size="icon"
              className="rounded-full bg-brand hover:bg-brand-hover text-brand-foreground"
              onClick={togglePlay}
              data-testid="replay-play-btn"
            >
              {playing ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
            </Button>
            <Button
              variant="ghost" size="icon" className="rounded-full"
              onClick={() => { setPlaying(false); setIdx((i) => Math.min(total - 1, i + 1)); }}
              data-testid="replay-next-btn"
            >
              <FastForward size={16} weight="fill" />
            </Button>
            <Button
              variant="ghost" size="icon" className="rounded-full"
              onClick={() => { setPlaying(false); setIdx(total - 1); }}
              data-testid="replay-end-btn"
            >
              <SkipForward size={16} weight="fill" />
            </Button>
          </div>
          <div className="flex items-center gap-1" data-testid="replay-speed-row">
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mr-1">Speed</span>
            {SPEEDS.map((s, i) => (
              <Button
                key={s.label}
                size="sm"
                variant={i === speedIdx ? "default" : "ghost"}
                onClick={() => setSpeedIdx(i)}
                className={`rounded-full h-7 px-2.5 text-xs ${i === speedIdx ? "bg-brand hover:bg-brand-hover text-brand-foreground" : ""}`}
                data-testid={`replay-speed-${i}`}
              >
                {s.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Frame info */}
        <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid="replay-frame-info">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-foreground">Round {frame?.round ?? 1}</span>
            <span>·</span>
            <span>{ACTION_LABEL[frame?.action] || frame?.action}</span>
            {actorName && <>
              <span>·</span>
              <span className={`font-semibold ${frame?.actor === "A" ? "text-[hsl(var(--chart-3))]" : "text-brand"}`}>
                {actorName}
              </span>
            </>}
            {frame?.action === "forfeit" && <Flag size={12} className="text-destructive" />}
          </div>
          {frame?.status === "completed" && (
            <span className="text-emerald-500 font-semibold">Final frame</span>
          )}
        </div>
      </div>
    </div>
  );
}
